import { type VariantProps, cva } from "class-variance-authority";
import { type ComponentType, type ReactNode, type SVGProps, useRef, useState } from "react";
import { AccessibleIcon } from "../../accessibility";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../overlay/Tooltip";
import { classNames } from "../../utils/classNames";

export const badgeVariants = cva(
	"inline-flex shrink-0 items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap rounded font-bold",
	{
		variants: {
			variant: {
				success: "bg-badge-success text-success",
				info: "bg-badge-info text-info",
				warning: "bg-badge-warning text-warning",
				critical: "bg-badge-critical text-critical",
				neutral: "bg-badge-neutral text-neutral",
				outline: "border border-neutral-faded bg-transparent text-neutral",
			},
			size: {
				sm: "px-1.5 py-0.5 text-xs",
				lg: "px-2.5 py-2 text-sm",
			},
		},
		defaultVariants: {
			variant: "neutral",
			size: "sm",
		},
	},
);

export type BadgeProps = {
	children: ReactNode;
	variant?: VariantProps<typeof badgeVariants>["variant"];
	size?: VariantProps<typeof badgeVariants>["size"];
	icon?: ComponentType<SVGProps<SVGSVGElement>>;
	className?: string;
};

export const Badge = ({ children, variant, size, icon: Icon, className }: BadgeProps) => {
	const ref = useRef<HTMLSpanElement>(null);
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Tooltip open={isOpen} onOpenChange={setIsOpen}>
			<TooltipTrigger>
				<span
					ref={ref}
					onMouseEnter={() => {
						if ((ref.current?.offsetWidth ?? 0) < (ref.current?.scrollWidth ?? 0)) {
							setIsOpen(true);
						}
					}}
					onMouseLeave={() => setIsOpen(false)}
					className={classNames(badgeVariants({ variant, size }), className)}
				>
					{Icon && (
						<AccessibleIcon>
							<Icon className={size === "lg" ? "size-3.5 shrink-0" : "size-3 shrink-0"} />
						</AccessibleIcon>
					)}
					{children}
				</span>
			</TooltipTrigger>
			<TooltipContent>{children}</TooltipContent>
		</Tooltip>
	);
};
